/** Scraper Job Console (v1 scrapers.js + scraper-builder.js). Restores the
 * ability to MANAGE the crawlers from v2: one list per source (Zillow /
 * InvestorLift / Price Monitor), an enable switch + delete per job, an org-wide
 * pause-all master switch, and the job builder form. Every field writes into a
 * draft `config` by the SAME dotted paths v1 used (location.*, schedule.*,
 * filters.* …) so a job saved here runs identically on the server cron. Writes
 * go through the Phase 21 door (crm2_scraper_job_apply); editing is gated behind
 * liveFire `scrapersEdit`. */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  cfgGet, cfgSet, cfgCsv, csvParse, jobInterval, jobStatusHue,
  JOB_SOURCES, type ScraperSource, type ScraperJob,
} from "./api";

const SOURCE_META: Record<ScraperSource, { name: string; sub: string }> = {
  zillow: { name: "Zillow Crawler", sub: "MLS / Zillow off-market scraper" },
  investorlift: { name: "InvestorLift Crawler", sub: "Wholesale marketplace scraper" },
  pricemonitor: { name: "Price Monitor", sub: "Tracks price changes on tracked listings" },
};
const SOURCE_TAB: Record<ScraperSource, string> = { zillow: "Zillow", investorlift: "InvestorLift", pricemonitor: "Price Monitor" };

type Field =
  | { k: "text"; p: string; label: string; ph?: string; def?: string; wide?: boolean }
  | { k: "csv"; p: string; label: string; ph?: string; wide?: boolean }
  | { k: "num"; p: string; label: string; ph?: string; def?: number }
  | { k: "select"; p: string; label: string; opts: string[]; def: string }
  | { k: "toggle"; p: string; label: string; sub?: string; def: boolean; wide?: boolean }
  | { k: "time"; p: string; label: string; def: string }
  | { k: "datetime"; p: string; label: string; wide?: boolean }
  | { k: "checks"; p: string; label: string; opts: string[]; defOn: string[]; wide?: boolean };
type Group = { title: string; fields: Field[] };

const IL_TYPES = ["Single-Family", "Multi-Family", "Townhouse", "Condo", "Land", "Manufactured", "Commercial", "Storage"];
const Z_TYPES = ["Houses", "Townhouses", "Condos", "Multi-Family", "Lots / Land", "Manufactured"];

function groupsFor(source: ScraperSource): Group[] {
  if (source === "zillow") return [
    { title: "Location", fields: [
      { k: "csv", p: "location.cities_or_zips", label: "Cities / Zip Codes", ph: "Orange, Volusia, …", wide: true },
      { k: "text", p: "location.state_code", label: "State Code", def: "FL" },
      { k: "toggle", p: "location.search_as_county", label: "Search as County", sub: "Treat the input as a county name", def: true },
    ] },
    { title: "Schedule", fields: [
      { k: "toggle", p: "schedule.repeat", label: "Repeat Job", def: true },
      { k: "num", p: "schedule.every", label: "Every", def: 23 },
      { k: "select", p: "schedule.unit", label: "Unit", opts: ["Minutes", "Hours"], def: "Minutes" },
      { k: "toggle", p: "schedule.quiet_on", label: "Quiet Hours (pause)", def: true },
      { k: "time", p: "schedule.quiet_hours.pause", label: "Pause at (ET)", def: "21:00" },
      { k: "time", p: "schedule.quiet_hours.resume", label: "Resume at (ET)", def: "07:00" },
      { k: "datetime", p: "schedule.scheduled_start", label: "Scheduled Start (optional)", wide: true },
    ] },
    { title: "Scraping Mode", fields: [
      { k: "toggle", p: "mode.index_only", label: "Index Only (cheap)", sub: "Skip per-listing detail calls.", def: false },
      { k: "toggle", p: "mode.deep_scan", label: "Deep Scan ($20k price chunks)", def: true },
      { k: "toggle", p: "outdated", label: "AI Interior Check", sub: "Flag outdated interiors (needs VISION_API_KEY).", def: false },
      { k: "num", p: "mode.max_items", label: "Max Items (0 = no cap)", ph: "e.g. 200" },
    ] },
    { title: "Integrations", fields: [
      // "Upload to CRM" (integrations.crm_upload) removed 2026-07-28. It was a
      // dead control: zharness_bridge hardcodes SinkConfig(enabled=True) and
      // nothing in server_code ever read the flag, so switching it OFF still
      // pushed every lead. A toggle that claims to gate a write path and does
      // not is worse than no toggle — leave the write on, remove the lie.
      // NOTE: this field becomes the JOB NAME on save (see doSave), and that name
      // is what push_to_supabase stamps onto properties.job_tag — which is what
      // the Properties job filter reads. Renaming it does NOT re-stamp existing
      // leads, so old leads keep the old tag and the filter shows both.
      { k: "text", p: "integrations.job_tag", label: "Job Tag", ph: "e.g. Zillow Flips Under 350k", wide: true },
    ] },
    { title: "Filters — Price & Size", fields: [
      { k: "num", p: "filters.price.min", label: "Min $" },
      { k: "num", p: "filters.price.max", label: "Max $ (0 = none)" },
      { k: "num", p: "filters.beds_min", label: "Min Beds", def: 2 },
      { k: "num", p: "filters.baths_min", label: "Min Baths", def: 1 },
      { k: "num", p: "filters.sqft.min", label: "Min SqFt" },
      { k: "num", p: "filters.sqft.max", label: "Max SqFt" },
      { k: "num", p: "filters.year_built.min", label: "Year Built ≥", def: 1950 },
      { k: "num", p: "filters.year_built.max", label: "Year Built ≤" },
      { k: "num", p: "filters.days_on_zillow.min", label: "Days on Zillow ≥", def: 0 },
      { k: "num", p: "filters.days_on_zillow.max", label: "Days on Zillow ≤" },
    ] },
    { title: "Filters — Types & Zips", fields: [
      { k: "checks", p: "filters.home_types", label: "Home Types", opts: Z_TYPES, defOn: ["Houses", "Townhouses", "Multi-Family"], wide: true },
      { k: "csv", p: "filters.counties", label: "Included Counties", ph: "Orange, Volusia, Lake…", wide: true },
      { k: "csv", p: "filters.keywords", label: "Description Keywords", ph: "pool, fixer, as-is…", wide: true },
      { k: "csv", p: "filters.included_zips", label: "Included Zips (whitelist)", ph: "32801, 32803…", wide: true },
      { k: "csv", p: "filters.excluded_zips", label: "Excluded Zips", ph: "33139, 33140…", wide: true },
      { k: "csv", p: "filters.excluded_brokers", label: "Excluded Brokers", ph: "Broker Name, …", wide: true },
    ] },
  ];
  if (source === "investorlift") return [
    { title: "Location", fields: [
      { k: "csv", p: "filters.states", label: "States", ph: "FL, TX", wide: true },
      { k: "csv", p: "filters.counties", label: "Counties", ph: "Orange, Hillsborough, Pasco…", wide: true },
    ] },
    { title: "Schedule", fields: [
      { k: "toggle", p: "schedule.repeat", label: "Repeat Job", def: true },
      { k: "num", p: "schedule.every", label: "Every", def: 60 },
      { k: "select", p: "schedule.unit", label: "Unit", opts: ["Minutes", "Hours"], def: "Minutes" },
      { k: "toggle", p: "schedule.quiet_on", label: "Quiet Hours (pause)", def: false },
      { k: "time", p: "schedule.quiet_hours.pause", label: "Pause at (ET)", def: "21:00" },
      { k: "time", p: "schedule.quiet_hours.resume", label: "Resume at (ET)", def: "07:00" },
    ] },
    { title: "Integrations", fields: [
      // crm_upload removed 2026-07-28 — see the Zillow section above. Dead flag.
      { k: "text", p: "integrations.job_tag", label: "Job Tag", ph: "e.g. IL-FL-Flips", wide: true },
      { k: "num", p: "mode.max_items", label: "Max Items (0 = no cap)", ph: "e.g. 200" },
      { k: "num", p: "filters.listing_age_max_min", label: "Max listing age (min)", def: 1440 },
    ] },
    { title: "Filters — Ranges", fields: [
      { k: "num", p: "filters.price.min", label: "Price Min $" },
      { k: "num", p: "filters.price.max", label: "Price Max $" },
      { k: "num", p: "filters.arv.min", label: "ARV Min $" },
      { k: "num", p: "filters.arv.max", label: "ARV Max $" },
      { k: "num", p: "filters.beds.min", label: "Beds Min" },
      { k: "num", p: "filters.beds.max", label: "Beds Max" },
      { k: "num", p: "filters.baths.min", label: "Baths Min" },
      { k: "num", p: "filters.baths.max", label: "Baths Max" },
      { k: "num", p: "filters.sqft.min", label: "SqFt Min" },
      { k: "num", p: "filters.sqft.max", label: "SqFt Max" },
      { k: "num", p: "filters.year_built.min", label: "Year Built ≥" },
      { k: "num", p: "filters.year_built.max", label: "Year Built ≤" },
    ] },
    { title: "Filters — Types & Zips", fields: [
      { k: "checks", p: "filters.property_types", label: "Property Types", opts: IL_TYPES, defOn: IL_TYPES.slice(0, 3), wide: true },
      { k: "csv", p: "filters.target_zips", label: "Target Zips (whitelist)", ph: "33101, 32801…", wide: true },
      { k: "csv", p: "filters.excluded_zips", label: "Excluded Zips", wide: true },
      { k: "csv", p: "filters.excluded_managers", label: "Excluded Managers", ph: "John Smith, ABC Realty…", wide: true },
    ] },
  ];
  return [ // pricemonitor
    { title: "Sources", fields: [
      { k: "toggle", p: "config.monitor_il", label: "Monitor InvestorLift", def: true },
      { k: "toggle", p: "config.monitor_zillow", label: "Monitor Zillow", def: true },
    ] },
    { title: "Schedule", fields: [
      { k: "toggle", p: "schedule.repeat", label: "Repeat", def: true },
      { k: "num", p: "schedule.every", label: "Check Every", def: 60 },
      { k: "select", p: "schedule.unit", label: "Unit", opts: ["Minutes", "Hours"], def: "Minutes" },
    ] },
    { title: "Check Limits", fields: [
      { k: "num", p: "monitor.max_checks", label: "Max Checks / run", def: 60 },
      { k: "num", p: "filters.drop_pct", label: "Drop Threshold (%)", def: 5 },
      { k: "toggle", p: "monitor.only_active", label: "Only Active Listings", sub: "Skip dead / lost / closed / archived.", def: true },
      { k: "select", p: "filters.notify_on", label: "Notify On", opts: ["Drops only", "Drops & Increases", "All changes"], def: "Drops & Increases" },
      { k: "text", p: "integrations.job_tag", label: "Job Tag", def: "Price Monitor" },
    ] },
  ];
}

/** Seed a NEW job's config with every field default (so the saved config carries
 * them, matching v1 where the form's shown defaults are collected on save). */
function seedDefaults(source: ScraperSource): Record<string, unknown> {
  let cfg: Record<string, unknown> = {};
  for (const g of groupsFor(source)) {
    for (const f of g.fields) {
      if (f.k === "toggle") cfg = cfgSet(cfg, f.p, f.def);
      else if (f.k === "select" || f.k === "time") cfg = cfgSet(cfg, f.p, f.def);
      else if (f.k === "checks") cfg = cfgSet(cfg, f.p, f.defOn);
      else if (f.k === "num" && f.def != null) cfg = cfgSet(cfg, f.p, f.def);
      else if (f.k === "text" && f.def != null) cfg = cfgSet(cfg, f.p, f.def);
    }
  }
  return cfg;
}

function chipsFor(j: ScraperJob): string[] {
  const cfg = j.config;
  const chips: string[] = [];
  const counties = (cfgGet(cfg, "filters.counties", null) ?? cfgGet(cfg, "location.counties", null) ?? cfgGet(cfg, "filters.states", null)) as unknown;
  const cArr = Array.isArray(counties) ? counties : [];
  if (cArr.length) chips.push(`${cArr.length} area${cArr.length > 1 ? "s" : ""}`);
  const pmin = Number(cfgGet(cfg, "filters.price.min", 0)) || 0;
  const pmax = Number(cfgGet(cfg, "filters.price.max", 0)) || 0;
  if (pmin || pmax) chips.push(`$${pmin.toLocaleString()}${pmax ? "–$" + pmax.toLocaleString() : "+"}`);
  const iv = jobInterval(cfg);
  if (iv !== "—") chips.push(iv);
  return chips;
}

export function ScraperJobsConsole({
  loadJobs, onSave, onToggle, onDelete, onPauseAll, canEdit,
}: {
  loadJobs: () => Promise<Record<ScraperSource, ScraperJob[]>>;
  onSave: (job: { id?: string; source: ScraperSource; name: string; config: Record<string, unknown>; enabled: boolean }) => Promise<string>;
  onToggle: (id: string, enabled: boolean) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onPauseAll: (enabled: boolean) => Promise<number>;
  canEdit: boolean;
}) {
  const [source, setSource] = useState<ScraperSource>("investorlift");
  const [groups, setGroups] = useState<Record<ScraperSource, ScraperJob[]> | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<null | { id?: string }>(null);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [enabledOnSave, setEnabledOnSave] = useState(false);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [err, setErr] = useState("");

  const refresh = useCallback(() => {
    setBusy(true);
    setErr("");
    void loadJobs()
      .then((g) => { setGroups(g); setErr(""); })
      // Leave groups as-is (null on first load) and SAY so. Collapsing a failed
      // read into three empty arrays rendered "No jobs yet — click New Job to
      // create one", which invites creating a duplicate of a job that is still
      // running server-side. `err` existed but only doSave ever wrote to it.
      .catch((e: unknown) => setErr(
        "Could not load jobs — " + (e instanceof Error ? e.message : "the backend did not answer") +
        ". Existing jobs may still be running; don't create duplicates until this loads."))
      .finally(() => setBusy(false));
  }, [loadJobs]);
  useEffect(() => { refresh(); }, [refresh]);

  const jobs = groups ? groups[source] : [];
  const allJobs = useMemo(() => (groups ? [...groups.zillow, ...groups.investorlift, ...groups.pricemonitor] : []), [groups]);
  const anyOn = allJobs.some((j) => j.enabled);

  const startNew = () => { setEditing({}); setDraft(seedDefaults(source)); setEnabledOnSave(false); setErr(""); };
  const startEdit = (j: ScraperJob) => { setSource(j.source); setEditing({ id: j.id }); setDraft(j.config); setEnabledOnSave(j.enabled); setErr(""); };
  const cancel = () => { setEditing(null); setErr(""); };

  const setPath = (path: string, val: unknown) => setDraft((d) => cfgSet(d, path, val));

  const doSave = async () => {
    if (!canEdit || !editing) return;
    setBusy(true); setErr("");
    try {
      const tag = String(cfgGet(draft, "integrations.job_tag", "")).trim();
      const name = tag || `${SOURCE_TAB[source]} job ${new Date().toLocaleDateString()}`;
      const base = { source, name, config: draft, enabled: enabledOnSave };
      await onSave(editing.id ? { ...base, id: editing.id } : base);
      setEditing(null);
      refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally { setBusy(false); }
  };

  const doToggle = async (j: ScraperJob) => {
    if (!canEdit) return;
    setBusy(true);
    try { await onToggle(j.id, !j.enabled); refresh(); } finally { setBusy(false); }
  };
  const doDelete = async (id: string) => {
    if (!canEdit) return;
    setConfirmDel(null); setBusy(true);
    try { if (editing?.id === id) setEditing(null); await onDelete(id); refresh(); } finally { setBusy(false); }
  };
  const doPauseAll = async () => {
    if (!canEdit || !allJobs.length) return;
    setBusy(true);
    try { await onPauseAll(!anyOn ? true : false); refresh(); } finally { setBusy(false); }
  };

  return (
    <div className="leads exppage">
      <div className="sctabs">
        {JOB_SOURCES.map((s) => (
          <button key={s} className={"sctab" + (source === s ? " active" : "")} onClick={() => { setSource(s); setEditing(null); }}>{SOURCE_TAB[s]}</button>
        ))}
      </div>

      <div className="exphead">
        <div>
          <h1>{SOURCE_META[source].name}</h1>
          <div className="scsub">{SOURCE_META[source].sub}</div>
        </div>
        <div className="expacts">
          {canEdit && allJobs.length ? (
            <button className={"switch" + (anyOn ? " on" : "")} onClick={doPauseAll} disabled={busy} title={anyOn ? "Pause every scraper job" : "Resume all"}>
              <span className="knob" />{anyOn ? "All running — pause" : "Resume all"}
            </button>
          ) : null}
          <button className="btn slim" onClick={refresh} disabled={busy}>{busy ? "…" : "↻ Refresh"}</button>
          {canEdit ? <button className="btn slim primary" onClick={startNew}>+ New Job</button> : null}
        </div>
      </div>

      {!canEdit ? (
        <div className="expqbo">Scraper management is view-only on this device. Jobs run server-side; the switch to manage them is turned on in the go-live sit-down.</div>
      ) : (
        <div className="expqbo">Create and control the server-side crawlers. Jobs run on their own schedule once enabled — the cron only runs jobs that are switched on. Pausing here stops new leads immediately.</div>
      )}

      {editing ? (
        <div className="expform scbuilder">
          <div className="expformtitle">{editing.id ? "Edit job" : "New " + SOURCE_TAB[source] + " job"}</div>
          {groupsFor(source).map((g) => (
            <div key={g.title} className="scgroup">
              <div className="scgrouptitle">{g.title}</div>
              <div className="expformgrid">
                {g.fields.map((f) => <FieldInput key={f.p} f={f} draft={draft} onChange={setPath} />)}
              </div>
            </div>
          ))}
          {err ? <div className="scerr">{err}</div> : null}
          <div className="expformacts">
            <button className="btn slim primary" onClick={doSave} disabled={busy}>{busy ? "Saving…" : (editing.id ? "Save changes" : "Create job")}</button>
            <label className="scenable"><input type="checkbox" checked={enabledOnSave} onChange={(e) => setEnabledOnSave(e.target.checked)} /> Enable on save</label>
            <button className="btn slim" onClick={cancel} disabled={busy}>Cancel</button>
          </div>
        </div>
      ) : null}

      <div className="scjoblist">
        <div className="scjobhead">Jobs ({groups === null ? "—" : jobs.length})</div>
        {groups === null && err ? (
          <div className="expempty">
            ⚠️ {err}
            <div><button className="btn slim" onClick={refresh} disabled={busy}>Try again</button></div>
          </div>
        ) : groups === null ? (
          <div className="expempty">Loading jobs…</div>
        ) : jobs.length === 0 ? (
          <div className="expempty">No {SOURCE_TAB[source]} jobs yet{canEdit ? " — click New Job to create one." : "."}</div>
        ) : (
          jobs.map((j) => (
            <div key={j.id} className="scjob">
              <div className="scjobmain">
                <div className="scjobname">{j.name}</div>
                <div className="scjobmeta">
                  <span className={"scstatus hue-" + jobStatusHue(j.status)}>{j.status}</span>
                  {chipsFor(j).map((c, i) => <span key={i} className="expcat">{c}</span>)}
                  {j.lastRun ? <span className="scjobwhen">ran {new Date(j.lastRun).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span> : <span className="scjobwhen">never run</span>}
                </div>
                {j.lastMessage ? <div className="scjobmsg">{j.lastMessage}</div> : null}
              </div>
              <div className="scjobacts">
                <button className={"switch" + (j.enabled ? " on" : "") + (canEdit ? "" : " locked")} onClick={() => doToggle(j)} disabled={busy || !canEdit} title={canEdit ? "" : "View-only"}>
                  <span className="knob" />{j.enabled ? "On" : "Off"}
                </button>
                {canEdit ? <button className="btn slim" onClick={() => startEdit(j)}>Edit</button> : null}
                {canEdit ? (
                  confirmDel === j.id
                    ? <button className="rowbtn del confirm" onClick={() => doDelete(j.id)}>Confirm?</button>
                    : <button className="rowbtn del" onClick={() => setConfirmDel(j.id)} title="Delete job">✕</button>
                ) : null}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function FieldInput({ f, draft, onChange }: { f: Field; draft: Record<string, unknown>; onChange: (p: string, v: unknown) => void }) {
  if (f.k === "toggle") {
    const on = cfgGet(draft, f.p, f.def) === true;
    return (
      <label className={"sctogfield" + (f.wide ? " wide" : "")}>
        <span className="sctoglabel">{f.label}{f.sub ? <span className="sctogsub">{f.sub}</span> : null}</span>
        <span className={"switch" + (on ? " on" : "")} onClick={() => onChange(f.p, !on)}><span className="knob" /></span>
      </label>
    );
  }
  if (f.k === "checks") {
    const cur = cfgGet(draft, f.p, f.defOn);
    const onList: string[] = Array.isArray(cur) ? (cur as string[]) : f.defOn;
    const toggle = (opt: string) => {
      const next = onList.includes(opt) ? onList.filter((x) => x !== opt) : [...onList, opt];
      onChange(f.p, next);
    };
    return (
      <label className={"scchecks" + (f.wide ? " wide" : "")}>
        <span>{f.label}</span>
        <div className="sccheckrow">
          {f.opts.map((o) => (
            <label key={o} className="sccheck"><input type="checkbox" checked={onList.includes(o)} onChange={() => toggle(o)} /> {o}</label>
          ))}
        </div>
      </label>
    );
  }
  const wide = "wide" in f && f.wide;
  if (f.k === "select") {
    const val = String(cfgGet(draft, f.p, f.def));
    return (
      <label className={wide ? "wide" : ""}>
        <span>{f.label}</span>
        <select value={val} onChange={(e) => onChange(f.p, e.target.value)}>
          {f.opts.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </label>
    );
  }
  if (f.k === "csv") {
    const val = cfgCsv(draft, f.p);
    return (
      <label className={wide ? "wide" : ""}>
        <span>{f.label}</span>
        <input value={val} placeholder={f.ph ?? ""} onChange={(e) => onChange(f.p, csvParse(e.target.value))} />
      </label>
    );
  }
  if (f.k === "time") {
    const val = String(cfgGet(draft, f.p, f.def));
    return (
      <label className={wide ? "wide" : ""}>
        <span>{f.label}</span>
        <input type="time" value={val} onChange={(e) => onChange(f.p, e.target.value)} />
      </label>
    );
  }
  if (f.k === "datetime") {
    const val = String(cfgGet(draft, f.p, ""));
    return (
      <label className={wide ? "wide" : ""}>
        <span>{f.label}</span>
        <input type="datetime-local" value={val} onChange={(e) => onChange(f.p, e.target.value)} />
      </label>
    );
  }
  if (f.k === "num") {
    const raw = cfgGet(draft, f.p, f.def ?? "");
    const val = raw === "" || raw == null ? "" : String(raw);
    return (
      <label className={wide ? "wide" : ""}>
        <span>{f.label}</span>
        <input type="number" value={val} placeholder={f.ph ?? ""} onChange={(e) => onChange(f.p, e.target.value === "" ? "" : Number(e.target.value))} />
      </label>
    );
  }
  // text
  const val = String(cfgGet(draft, f.p, f.def ?? ""));
  return (
    <label className={wide ? "wide" : ""}>
      <span>{f.label}</span>
      <input value={val} placeholder={f.ph ?? ""} onChange={(e) => onChange(f.p, e.target.value)} />
    </label>
  );
}
